/**
 * DOIS SERVIÇOS QUE NINGUÉM CONSEGUE DISTINGUIR.
 *
 * O pedido era "checar se o serviço já existe", e a caça aos pares reais mudou a
 * regra. Na base há seis pares de serviços do mesmo cliente, e as tentativas
 * óbvias erram:
 *
 *   mesma data              o Do Vela tem dois serviços em 20/08, com lotes,
 *                           etapas e modelos diferentes. São dois trabalhos.
 *   mesmas etapas           acerta quatro dos seis pares. Avisar em quatro para
 *                           pegar um é ensinar a ignorar o aviso.
 *   criados em sequência    cadastrar dois serviços do mesmo cliente em
 *                           oitenta segundos é fluxo normal ali.
 *
 * O que separa o par suspeito dos legítimos é OUTRA coisa: nos legítimos, o lote
 * está preenchido e é diferente — "VITOR VENTURA" e "VITOR VENTURA AGUAS
 * CLARAS" são duas fazendas do mesmo dono. No par suspeito, os dois estão SEM
 * LOTE, com as mesmas etapas e o mesmo modelo: não existe nada, em nenhuma tela,
 * que diga qual é qual.
 *
 * Então o aviso não afirma "isto é duplicata", que ele não tem como saber. Ele
 * afirma o que é verificável: ESTE SERVIÇO FICARIA INDISTINGUÍVEL DE UM QUE JÁ
 * EXISTE. E a saída que ele oferece é dar um lote — que é o que resolve mesmo se
 * os dois forem legítimos.
 *
 * É por isso também que o lote continua opcional: o aviso pede o nome no momento
 * em que o nome passa a fazer falta, em vez de exigir sempre.
 */

/** Só o que distingue: espaço e caixa não são nome. */
const nome = (v) => String(v ?? '').trim().toUpperCase();

const temLote = (s) => nome(s?.service_label).length > 0;

const etapas = (s) => [...(s?.services_offered || [])].map(nome).sort().join('|');

const dia = (v) => (v ? String(v).slice(0, 10) : null);

const diasEntre = (a, b) => {
  const x = dia(a);
  const y = dia(b);
  if (!x || !y) return null;
  return Math.abs(Math.round(
    (Date.parse(`${x}T00:00:00Z`) - Date.parse(`${y}T00:00:00Z`)) / 86400000));
};

/**
 * A janela em que a data não distingue dois serviços sem nome.
 *
 * Um ciclo de FIV vai da OPU ao DG60, uns setenta dias. Dentro do mesmo ciclo,
 * "o do dia 15" e "o do dia 24" do mesmo cliente são confundíveis quando nenhum
 * dos dois tem nome; a três meses de distância, a data já resolve sozinha.
 *
 * Trinta dias é o meio disso, e é um botão: aumentar avisa mais e incomoda mais.
 */
export const JANELA_DE_CONFUSAO = 30;

/**
 * Os serviços de que este ficaria indistinguível.
 *
 * `servico` pode ser o formulário ainda não salvo — só precisa de `client_id`,
 * `date`, `services_offered`, `billing_model` e `service_label`.
 *
 * Devolve lista, e não um booleano, porque a tela precisa mostrar QUAIS para a
 * pessoa reconhecer o seu serviço e abrir em vez de criar outro.
 */
export const servicosIndistinguiveis = (servico, services = []) => {
  if (!servico?.client_id) return [];
  // Com lote, este serviço tem nome próprio: não há o que confundir, mesmo que
  // exista outro igual em tudo mais.
  if (temLote(servico)) return [];

  const minhasEtapas = etapas(servico);
  if (!minhasEtapas) return [];

  return (services || []).filter(outro => {
    if (!outro || outro.id === servico.id) return false;
    if (outro.client_id !== servico.client_id) return false;
    // O outro tem nome: dá para dizer qual é qual.
    if (temLote(outro)) return false;
    if (etapas(outro) !== minhasEtapas) return false;
    if (nome(outro.billing_model) !== nome(servico.billing_model)) return false;

    const distancia = diasEntre(outro.date, servico.date);
    // Sem data em um dos dois, a data não distingue nada — então conta como perto.
    return distancia === null || distancia <= JANELA_DE_CONFUSAO;
  });
};

/**
 * O QUE JÁ EXISTE DAQUELE CLIENTE, para a tela mostrar antes de criar.
 *
 * Isto não é aviso, é contexto, e é o que de fato previne o erro humano: a pessoa
 * vê que já existe enquanto ainda está preenchendo, e abre em vez de criar. O
 * aviso do salvar chega tarde — quem duplica está confiante, e clica.
 *
 * Em ANDAMENTO primeiro e por data mais recente: o serviço que a pessoa pode
 * estar recadastrando é o que está aberto agora, não o que fechou em março.
 */
export const servicosDoCliente = (clientId, services = [], { concluido } = {}) => {
  if (!clientId) return [];

  const ehConcluido = typeof concluido === 'function' ? concluido : () => false;

  return (services || [])
    .filter(s => s?.client_id === clientId)
    .map(s => ({ service: s, fechado: ehConcluido(s) }))
    .sort((a, b) => (a.fechado === b.fechado
      ? String(b.service.date || '').localeCompare(String(a.service.date || ''))
      : (a.fechado ? 1 : -1)))
    .map(x => x.service);
};
